import { useState } from "react";
import api, { useList, errMsg, fmtRM, MONTH_NOW, apiFileUrl } from "@/lib/api";
import { PageHeader, StatusBadge, Spinner, EmptyState, Money } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { FilePlus2, Banknote, BellRing, FileDown, ShieldAlert, FileWarning } from "lucide-react";

const WAIVER_TYPE_OPTIONS = [
  { v: "sibling_enrolment", label: "Sibling enrolment" },
  { v: "returning_student", label: "Returning student" },
  { v: "promotion_campaign", label: "Promotion / campaign" },
  { v: "management_approval", label: "Management approval" },
  { v: "staff_family", label: "Staff family" },
  { v: "correction_error", label: "Correction / data error" },
  { v: "special_case", label: "Special case" },
  { v: "other", label: "Other (type reason)" },
];

function WaiveDialog({ open, onOpenChange, kind, invoiceId, onDone }) {
  // kind: "registration" | "deposit"
  const isDeposit = kind === "deposit";
  const [reason, setReason] = useState("");
  const [waiverType, setWaiverType] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);

  const reset = () => { setReason(""); setWaiverType(""); setConfirmed(false); };
  const close = (o) => { if (!o) reset(); onOpenChange(o); };

  const submit = async () => {
    if (!reason.trim()) { toast.error("Waiver reason is required"); return; }
    if (!isDeposit && !waiverType) { toast.error("Pick a waiver type"); return; }
    if (isDeposit && !confirmed) { toast.error("Tick the confirmation checkbox"); return; }
    setBusy(true);
    try {
      const path = isDeposit
        ? `/invoices/${invoiceId}/waive-deposit`
        : `/invoices/${invoiceId}/waive-registration-fee`;
      const { data } = await api.post(path, {
        reason, waiver_type: isDeposit ? null : waiverType, confirmed: isDeposit ? true : false,
      });
      toast.success(`${isDeposit ? "Deposit" : "Registration fee"} waived (${fmtRM(data.waived_amount)})`);
      reset();
      onDone && onDone();
      onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid={`waive-${kind}-dialog`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isDeposit ? <ShieldAlert className="h-5 w-5 text-[#D90429]" /> : <FileWarning className="h-5 w-5 text-amber-600" />}
            {isDeposit ? "Waive non-refundable deposit (Super Admin)" : "Waive registration fee"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          {isDeposit ? (
            <div className="rounded-md border border-[#D90429]/30 bg-[#D90429]/5 p-3 text-xs leading-relaxed">
              <p className="font-semibold text-[#D90429]">⚠ Deposit is normally required and non-refundable.</p>
              <p className="mt-1">Waiving the deposit is a rare exception and is recorded in the audit log against your Super Admin user ID.</p>
            </div>
          ) : (
            <div className="rounded-md border bg-muted p-3 text-xs leading-relaxed">
              <p>Registration fee is charged by default. Waiver requires a reason and waiver type. Recorded in the audit log.</p>
            </div>
          )}
          {!isDeposit && (
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Waiver type</Label>
              <Select value={waiverType} onValueChange={setWaiverType}>
                <SelectTrigger data-testid="waiver-type-select"><SelectValue placeholder="Pick a type…" /></SelectTrigger>
                <SelectContent>
                  {WAIVER_TYPE_OPTIONS.map((o) => (
                    <SelectItem key={o.v} value={o.v} data-testid={`waiver-type-${o.v}`}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Reason</Label>
            <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
              data-testid="waiver-reason-input"
              placeholder={isDeposit ? "Explain why the deposit is being waived…" : "Explain the waiver context…"} />
          </div>
          {isDeposit && (
            <label className="flex items-start gap-2 cursor-pointer" data-testid="waiver-confirm-label">
              <input type="checkbox" className="mt-1 h-4 w-4 accent-[#D90429]"
                checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)}
                data-testid="waiver-confirm-checkbox" />
              <span className="text-xs">I confirm this Super Admin deposit waiver and accept the audit-log entry.</span>
            </label>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => close(false)} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className={isDeposit ? "bg-[#D90429] hover:bg-[#a8021f]" : ""}
                  data-testid="waiver-submit">
            {busy ? "Waiving…" : isDeposit ? "Waive deposit" : "Waive registration fee"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function InvoiceDetailDialog({ invoiceId, open, onOpenChange, onChanged, readOnly = false }) {
  const { data: inv, loading, reload } = useList(open && invoiceId ? `/invoices/${invoiceId}` : null, [invoiceId, open]);
  const { user } = useAuth();
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("bank_transfer");
  const [refNo, setRefNo] = useState("");
  const [busy, setBusy] = useState(false);
  const [waiveKind, setWaiveKind] = useState(null); // "registration" | "deposit" | null

  if (!open) return null;

  const recordPayment = async () => {
    if (!amount) { toast.error("Enter the payment amount"); return; }
    setBusy(true);
    try {
      await api.post("/payments", { invoice_id: invoiceId, amount: parseFloat(amount), payment_method: method, reference_no: refNo || null });
      toast.success("Payment recorded");
      setAmount(""); setRefNo("");
      reload();
      onChanged && onChanged();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="invoice-detail-dialog">
        {loading || !inv ? <Spinner full /> : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display flex items-center gap-3">
                {inv.invoice_number} <StatusBadge status={inv.status} testId="invoice-status-badge" />
                <Button variant="outline" size="sm" className="ml-auto"
                  onClick={() => window.open(apiFileUrl(`/invoices/${inv.id}/pdf`), "_blank")}
                  data-testid="download-invoice-pdf">
                  <FileDown className="h-4 w-4 mr-1" />PDF
                </Button>
              </DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground -mt-2">{inv.student_name} · {inv.invoice_month} · Due {inv.due_date}</p>
            <Table>
              <TableHeader><TableRow className="hover:bg-transparent">
                <TableHead>Item</TableHead><TableHead>Amount</TableHead>
                <TableHead>SST</TableHead><TableHead className="text-right">Total</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {inv.items.map((it) => {
                  const kind = it.item_kind || "tuition";
                  const isDepositOrAdj = ["deposit", "deposit_initial", "deposit_topup", "adjustment", "refund_credit"].includes(kind);
                  const lineAmount = isDepositOrAdj
                    ? (it.deposit_amount ?? it.subtotal_before_sst ?? 0)
                    : ((it.music_course_fee || 0) + (it.school_admin_fee || 0));
                  return (
                    <TableRow key={it.id} className={it.waived ? "opacity-60" : ""} data-testid={`invoice-item-${kind}`}>
                      <TableCell className="text-sm">
                        {it.description}
                        {it.waived && (
                          <div className="text-[10px] text-amber-700 italic mt-0.5" data-testid={`waived-meta-${it.item_kind}`}>
                            Original: {fmtRM(it.original_amount)} · Waiver: {it.waiver_type || "—"} · {it.waiver_reason}
                          </div>
                        )}
                      </TableCell>
                      <TableCell><Money value={lineAmount} /></TableCell>
                      <TableCell><Money value={it.sst_amount} /></TableCell>
                      <TableCell className="text-right"><Money value={it.total_amount_including_sst} /></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <div className="text-sm font-mono space-y-1 bg-muted rounded-md p-4" data-testid="invoice-totals">
              <p>Subtotal before SST: {fmtRM(inv.subtotal_before_sst)}</p>
              <p>SST ({Math.round(inv.sst_rate * 10000) / 100}%): {fmtRM(inv.sst_amount)}</p>
              {Math.abs(inv.rounding_adjustment || 0) >= 0.005 && (
                <p data-testid="invoice-rounding-adj">Round. adj. (5 sen): {inv.rounding_adjustment < 0 ? `(${fmtRM(Math.abs(inv.rounding_adjustment))})` : fmtRM(inv.rounding_adjustment)}</p>
              )}
              <p className="font-bold">Total: {fmtRM(inv.total_fees_collected)}</p>
              <p className="text-[#2D6A4F]">Paid: {fmtRM(inv.paid_amount)}</p>
              <p className="text-[#D90429]">Outstanding: {fmtRM(inv.outstanding_amount)}</p>
            </div>
            {inv.payments.length > 0 && (
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">Payments</p>
                {inv.payments.map((p) => (
                  <p key={p.id} className="text-sm py-1 border-b last:border-0">
                    {p.payment_date} — <Money value={p.amount} /> via {p.payment_method.replace("_", " ")} {p.reference_no && `(${p.reference_no})`}
                  </p>
                ))}
              </div>
            )}
            {!readOnly && inv.status !== "paid" && (
              <div className="border rounded-md p-4 space-y-3">
                <p className="text-sm font-semibold flex items-center gap-1.5"><Banknote className="h-4 w-4 text-secondary" />Record payment</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Amount (RM)</Label>
                    <Input data-testid="payment-amount-input" type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
                      placeholder={String(inv.outstanding_amount)} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Method</Label>
                    <Select value={method} onValueChange={setMethod}>
                      <SelectTrigger data-testid="payment-method-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="bank_transfer">Bank transfer</SelectItem>
                        <SelectItem value="cash">Cash</SelectItem>
                        <SelectItem value="card">Card</SelectItem>
                        <SelectItem value="ewallet">E-wallet</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Reference</Label>
                    <Input data-testid="payment-reference-input" value={refNo} onChange={(e) => setRefNo(e.target.value)} placeholder="TXN-..." />
                  </div>
                </div>
                <Button onClick={recordPayment} disabled={busy} data-testid="record-payment-button">
                  {busy ? "Recording..." : "Record payment"}
                </Button>
              </div>
            )}
            {!readOnly && user && ["admin", "super_admin"].includes(user.role) && (() => {
              const regItem = inv.items.find((it) => it.item_kind === "registration" && !it.waived);
              const depItem = inv.items.find((it) => it.item_kind === "deposit" && !it.waived);
              if (!regItem && !depItem) return null;
              return (
                <div className="border rounded-md p-4 space-y-2 bg-amber-50/30" data-testid="waiver-actions">
                  <p className="text-sm font-semibold flex items-center gap-1.5">
                    <FileWarning className="h-4 w-4 text-amber-600" />Fee waivers (audit-logged)
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {regItem && (
                      <Button size="sm" variant="outline" className="border-amber-500/40"
                        onClick={() => setWaiveKind("registration")}
                        data-testid="open-waive-registration">
                        Waive registration fee ({fmtRM(regItem.total_amount_including_sst)})
                      </Button>
                    )}
                    {depItem && (
                      user.role === "super_admin" ? (
                        <Button size="sm" variant="outline" className="border-[#D90429]/40 text-[#D90429] hover:bg-[#D90429]/5"
                          onClick={() => setWaiveKind("deposit")}
                          data-testid="open-waive-deposit">
                          <ShieldAlert className="h-3.5 w-3.5 mr-1" />Waive non-refundable deposit ({fmtRM(depItem.total_amount_including_sst)})
                        </Button>
                      ) : (
                        <span className="text-xs text-muted-foreground italic self-center" data-testid="deposit-waiver-locked">
                          Non-refundable deposit · waiver requires Super Admin
                        </span>
                      )
                    )}
                  </div>
                </div>
              );
            })()}
          </>
        )}
      </DialogContent>
      {waiveKind && (
        <WaiveDialog open={!!waiveKind} onOpenChange={(o) => !o && setWaiveKind(null)}
          kind={waiveKind} invoiceId={invoiceId}
          onDone={() => { reload(); onChanged && onChanged(); }} />
      )}
    </Dialog>
  );
}

export function InvoicesPage() {
  const [month, setMonth] = useState(MONTH_NOW);
  const { data: invoices, loading, reload } = useList(`/invoices?month=${month}`, [month]);
  const [selected, setSelected] = useState(null);
  const [generating, setGenerating] = useState(false);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/invoices/generate", { month });
      toast.success(`Generated ${data.created.length} invoices, skipped ${data.skipped.length}`);
      reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setGenerating(false); }
  };

  return (
    <div data-testid="invoices-page">
      <PageHeader title="Invoices"
        description="Monthly invoices: Music Course Fee + School Admin Fee + SST. Generated invoices snapshot the rates in force."
        actions={
          <>
            <Input type="month" data-testid="invoice-month-input" className="w-44" value={month} onChange={(e) => setMonth(e.target.value)} />
            <Button onClick={generate} disabled={generating} data-testid="generate-invoices-button">
              <FilePlus2 className="h-4 w-4 mr-1" /> {generating ? "Generating..." : "Generate for month"}
            </Button>
          </>
        } />
      <div className="rounded-lg border bg-card overflow-x-auto">
        {loading ? <div className="p-12 flex justify-center"><Spinner /></div> :
          !invoices || invoices.length === 0 ? <div className="p-6"><EmptyState text="No invoices for this month yet. Click 'Generate for month'." /></div> : (
          <Table>
            <TableHeader><TableRow className="hover:bg-transparent">
              <TableHead>Invoice #</TableHead><TableHead>Student</TableHead><TableHead>Due</TableHead>
              <TableHead>Total</TableHead><TableHead>Paid</TableHead><TableHead>Outstanding</TableHead><TableHead>Status</TableHead>
            </TableRow></TableHeader>
            <TableBody className="stagger-in">
              {invoices.map((i) => (
                <TableRow key={i.id} className="cursor-pointer" onClick={() => setSelected(i.id)} data-testid={`invoice-row-${i.id}`}>
                  <TableCell className="font-mono text-xs">{i.invoice_number}</TableCell>
                  <TableCell className="font-medium">{i.student_name}</TableCell>
                  <TableCell className="text-sm">{i.due_date}</TableCell>
                  <TableCell><Money value={i.total_fees_collected} /></TableCell>
                  <TableCell><Money value={i.paid_amount} /></TableCell>
                  <TableCell><Money value={i.outstanding_amount} /></TableCell>
                  <TableCell>
                    <div className="flex gap-1.5">
                      <StatusBadge status={i.status} />
                      {i.is_overdue && <StatusBadge status="overdue" />}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
      <InvoiceDetailDialog invoiceId={selected} open={!!selected} onOpenChange={(o) => !o && setSelected(null)} onChanged={reload} />
    </div>
  );
}

export function PaymentsPage() {
  const { data: payments, loading } = useList("/payments");
  const { data: reminders, loading: remLoading, reload: reloadReminders } = useList("/payments/reminders");
  const [remBusy, setRemBusy] = useState(false);

  const sendReminders = async (ids) => {
    setRemBusy(true);
    try {
      const { data } = await api.post("/payments/reminders/send", { invoice_ids: ids });
      toast.success(`Reminder sent for ${data.reminded} invoice${data.reminded === 1 ? "" : "s"} — see Notification center for delivery status`);
      reloadReminders();
    } catch (e) { toast.error(errMsg(e)); } finally { setRemBusy(false); }
  };

  return (
    <div data-testid="payments-page">
      <PageHeader title="Payments" description="Collected payments and the reminder list of unpaid / overdue invoices." />
      <Tabs defaultValue="payments">
        <TabsList className="h-auto mb-6">
          <TabsTrigger value="payments" data-testid="tab-payments">Payments received</TabsTrigger>
          <TabsTrigger value="reminders" data-testid="tab-reminders">Payment reminders</TabsTrigger>
        </TabsList>
        <TabsContent value="payments">
          <div className="rounded-lg border bg-card overflow-x-auto">
            {loading ? <div className="p-12 flex justify-center"><Spinner /></div> :
              !payments || payments.length === 0 ? <div className="p-6"><EmptyState /></div> : (
              <Table>
                <TableHeader><TableRow className="hover:bg-transparent">
                  <TableHead>Date</TableHead><TableHead>Invoice</TableHead><TableHead>Student</TableHead>
                  <TableHead>Method</TableHead><TableHead>Reference</TableHead><TableHead className="text-right">Amount</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {payments.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell className="text-sm">{p.payment_date}</TableCell>
                      <TableCell className="font-mono text-xs">{p.invoice_number}</TableCell>
                      <TableCell>{p.student_name}</TableCell>
                      <TableCell className="capitalize">{(p.payment_method || "").replace("_", " ")}</TableCell>
                      <TableCell className="font-mono text-xs">{p.reference_no || "—"}</TableCell>
                      <TableCell className="text-right"><Money value={p.amount} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </TabsContent>
        <TabsContent value="reminders">
          {reminders && reminders.length > 0 && (
            <div className="flex justify-end mb-3">
              <Button variant="outline" size="sm" disabled={remBusy}
                onClick={() => sendReminders(reminders.map((r) => r.id))}
                data-testid="send-all-reminders-button">
                <BellRing className="h-4 w-4 mr-1" />{remBusy ? "Sending..." : "Send all reminders"}
              </Button>
            </div>
          )}
          <div className="rounded-lg border bg-card overflow-x-auto" data-testid="reminders-table">
            {remLoading ? <div className="p-12 flex justify-center"><Spinner /></div> :
              !reminders || reminders.length === 0 ? <div className="p-6"><EmptyState text="No unpaid invoices. Well done!" /></div> : (
              <Table>
                <TableHeader><TableRow className="hover:bg-transparent">
                  <TableHead>Invoice</TableHead><TableHead>Student</TableHead><TableHead>Parent contact</TableHead>
                  <TableHead>Due</TableHead><TableHead>Outstanding</TableHead><TableHead>Status</TableHead>
                  <TableHead></TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {reminders.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="font-mono text-xs">{r.invoice_number}</TableCell>
                      <TableCell className="font-medium">{r.student_name}</TableCell>
                      <TableCell className="text-sm">{r.parent_name}<br /><span className="text-xs text-muted-foreground">{r.parent_phone}</span></TableCell>
                      <TableCell className="text-sm">{r.due_date}</TableCell>
                      <TableCell><Money value={r.outstanding_amount} className="text-[#D90429]" /></TableCell>
                      <TableCell>
                        <div className="flex gap-1.5">
                          <StatusBadge status={r.status} />
                          {r.is_overdue && <StatusBadge status="overdue" />}
                        </div>
                        {r.last_reminder_at && (
                          <p className="text-[11px] text-muted-foreground mt-1">
                            Last reminded {r.last_reminder_at.slice(0, 10)}
                          </p>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" disabled={remBusy}
                          onClick={() => sendReminders([r.id])} data-testid={`send-reminder-${r.id}`}>
                          <BellRing className="h-4 w-4 mr-1" />Remind
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
